import { useState, memo } from "react";
import { Maximize2 } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

import { Box, Heading, Text, Flex, HStack, Button, Dialog, CloseButton } from "@libs/design";

const DomainTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    payload?: { name: string; value: number; color: string };
  }>;
}) => {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  const name = item.payload?.name ?? item.name ?? "—";
  const value = item.value ?? 0;
  const color = (item.payload as { color?: string })?.color ?? "#6B7280";
  return (
    <Box
      bg="white"
      borderRadius="md"
      borderWidth="1px"
      borderColor="gray.200"
      boxShadow="md"
      px={3}
      py={2.5}
      minW="100px"
    >
      <Flex align="center" gap={2}>
        <Box w={2.5} h={2.5} borderRadius="full" style={{ backgroundColor: color }} />
        <Text fontWeight="semibold" color="gray.800" style={{ fontSize: "11px" }}>
          {name}
        </Text>
      </Flex>
      <Text mt={1} color="gray.600" style={{ fontSize: "10px" }}>
        Completeness: <strong>{value}%</strong>
      </Text>
    </Box>
  );
};

export type DomainBreakdownChartProps = {
  data: Array<{ name: string; value: number; color: string }>;
};

export const DomainBreakdownChart = memo(function DomainBreakdownChart({
  data,
}: DomainBreakdownChartProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const renderChart = (size: number, isModal: boolean) => {
    const isLarge = size >= 350;
    const outerRadius = isLarge ? 130 : 85;
    const innerRadius = isLarge ? 65 : 45;

    return (
      <Flex direction="column" align="center" w="100%" minH={size}>
        {/* Chart */}
        <Box w="100%" h={size}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 20, right: 40, bottom: 20, left: 40 }}>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                dataKey="value"
                label={({ value }: { value?: number }) => `${value}%`}
                style={{ fontSize: isLarge ? "11px" : "9px" }}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>

              <Tooltip content={<DomainTooltip />} wrapperStyle={{ zIndex: 1000 }} />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        <Flex wrap="wrap" justify="center" gap={isLarge ? 4 : 3} mt={4} maxW="100%">
          {data.map((item, i) => (
            <HStack key={i} gap={2}>
              <Box w={3} h={3} borderRadius="full" bg={item.color} flexShrink={0} />
              <Text fontSize={isModal ? "sm" : "xs"} color="gray.600">
                {item.name}
              </Text>
            </HStack>
          ))}
        </Flex>
      </Flex>
    );
  };

  return (
    <>
      <Box p={6}>
        <Flex justify="space-between" align="center" mb={6}>
          <Box gap={0}>
            <Heading fontSize="md" fontWeight="semibold" color="gray.900">
              Domain Breakdown
            </Heading>
            <Text fontSize="xs" color="gray.500">
              Current completeness by domain
            </Text>
          </Box>
          <Button
            size="xs"
            variant="ghost"
            onClick={() => setIsExpanded(true)}
            style={{ color: "#6B7280" }}
            _hover={{ backgroundColor: "#F3F4F6" }}
            _focus={{ boxShadow: "none", outline: "none" }}
            _focusVisible={{ boxShadow: "none", outline: "none" }}
          >
            <Maximize2 size={16} />
          </Button>
        </Flex>
        {renderChart(240, false)}
      </Box>

      <Dialog.Root open={isExpanded} onOpenChange={(e) => setIsExpanded(e.open)}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxW="7xl" style={{ backgroundColor: "#FFFFFF" }}>
            <Dialog.Header>
              <Dialog.Title style={{ color: "#111827", fontSize: "1rem", fontWeight: 600 }}>
                Domain Breakdown
              </Dialog.Title>
              <Dialog.CloseTrigger>
                <CloseButton bg="transparent" />
              </Dialog.CloseTrigger>
            </Dialog.Header>
            <Dialog.Body>{renderChart(380, true)}</Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </>
  );
});
